import { use, useCallback, useState } from 'react';

import dayjs from 'dayjs';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { KeyedMutator } from 'swr';

import FormDialog from '~components/dialogs/formDialog';
import ExportForm, {
	getExportFormatExtension,
	type ExportFormat,
	type ExportStatus,
} from '~components/forms/exportForm';
import { queryParameters } from '~constants';
import { UserRoleEnum } from '~enums';
import OrganisationsService from '~features/organisations/services/organisationsService';
import { ResourceGroupService } from '~features/resourceGroups';
import useAuthorize from '~hooks/useAuthorize';
import { OffsetPagedResults } from '~interfaces';
import ExportService from '~services/exportService';
import { getService } from '~services/serviceFactory';

import Key from '../../../interfaces/key';
import KeysDataGrid from '../components/dataGrids/keysDataGrid';
import KeyMultiAssignDialog from '../components/dialogs/keyMultiAssignDialogs';
import KeysListType from '../interfaces/keysListType';
import SelectedKeysContext from '../providers/selectedKeysContext';

interface Props {
	keysListType: KeysListType;
}

type UserAction = {
	type: 'assignKeys' | 'export';
	mutate?: KeyedMutator<OffsetPagedResults<Key>>;
	clearSelection?: () => void;
} | null;

const organisationService = getService(OrganisationsService);
const resourceGroupService = getService(ResourceGroupService);
const exportService = getService(ExportService);

const KeysOverviewContainer = ({ keysListType }: Props) => {
	const { t } = useTranslation();
	const { enqueueSnackbar } = useSnackbar();
	const selectedKeysContext = use(SelectedKeysContext);
	const [userAction, setUserAction] = useState<UserAction | null>(null);
	const [exportStatus, setExportStatus] = useState<ExportStatus>('selecting');
	const [selectedFormat, setSelectedFormat] = useState<ExportFormat | null>(null);
	const [showModalLoader, setShowModalLoader] = useState(false);
	const [searchParams] = useSearchParams();
	const allowsExporting = useAuthorize().authorize([
		UserRoleEnum.SuperAdmin,
		UserRoleEnum.Support,
		UserRoleEnum.Manufacturing,
		UserRoleEnum.ClientAdmin,
	]);

	const clearUserAction = () => {
		setUserAction(null);
		setTimeout(() => {
			setExportStatus('selecting');
			setSelectedFormat(null);
		}, 300);
	};

	const handleAssign = (
		mutate: KeyedMutator<OffsetPagedResults<Key>>,
		clearSelection: () => void
	) => {
		setUserAction({
			type: 'assignKeys',
			mutate,
			clearSelection,
		});
	};

	const handleAssignKeys = (
		organisationId: string,
		resourceGroupId: string | undefined,
		closeDialog: () => void
	) => {
		const keyIds =
			selectedKeysContext?.singleKey ?
				[selectedKeysContext?.singleKey.id]
			:	(selectedKeysContext?.selectedKeys?.flatMap((value) => value.id) ?? []);
		const isSingleKey = keyIds.length == 1;
		const successValue =
			isSingleKey ?
				t('keyAssignedSuccesfully', { ns: 'toast' })
			:	t('keysAssignedSuccesfully', { ns: 'toast' });
		const failedValue =
			isSingleKey ?
				t('keyFailedToAssign', { ns: 'toast' })
			:	t('keysFailedToAssign', { ns: 'toast' });

		if (!keyIds) {
			enqueueSnackbar(failedValue, { variant: 'error' });
			return;
		}

		setShowModalLoader(true);
		organisationService
			.assignKeysToOrganisation(keyIds, organisationId)
			.then(() => {
				if (resourceGroupId) {
					resourceGroupService
						.assignKeysToResourceGroup(keyIds, resourceGroupId, organisationId)
						.then(() => {
							cleanUpAfterDialog(closeDialog);
							enqueueSnackbar(successValue, { variant: 'success' });
						})
						.catch((_reason) => {
							enqueueSnackbar(failedValue, { variant: 'error' });
						});
				} else {
					cleanUpAfterDialog(closeDialog);
					enqueueSnackbar(successValue, { variant: 'success' });
				}
			})
			.catch((_reason) => {
				enqueueSnackbar(t('failedToAssignToOrganisation', { ns: 'errors' }), { variant: 'error' });
			})
			.finally(() => {
				setShowModalLoader(false);
			});
	};

	const cleanUpAfterDialog = (closeDialog?: () => void) => {
		setUserAction(null);
		userAction?.mutate?.();
		userAction?.clearSelection?.();
		selectedKeysContext.updateSingleKey(undefined);
		closeDialog?.();
	};

	function triggerBlobDownload(blob: Blob, fileName: string) {
		const url = URL.createObjectURL(blob);
		const a = document.createElement('a');
		a.href = url;
		a.download = fileName;
		document.body.appendChild(a);
		a.click();
		document.body.removeChild(a);
		URL.revokeObjectURL(url);
	}

	const handleExport = useCallback(
		async (format: ExportFormat) => {
			setExportStatus('exporting');

			try {
				const search = searchParams.get(queryParameters.SearchQuery) ?? undefined;
				const keyType = searchParams.get(queryParameters.KeyType) ?? undefined;
				const resourceGroupId = searchParams.get(queryParameters.ResourceGroupId) ?? undefined;
				const clientId = searchParams.get(queryParameters.OrganisationId) ?? undefined;

				const blob = await exportService.exportKeys({
					format,
					search,
					type: keyType,
					resourceGroupId,
					clientId,
				});

				const fileName = `${dayjs().format('DD-MM-YYYY_HH-mm')}_keys_export${getExportFormatExtension(format)}`;
				triggerBlobDownload(blob, fileName);

				enqueueSnackbar(t('exportGeneratedSuccessfully', { ns: 'toast' }), { variant: 'success' });
			} catch (error) {
				console.error('Export failed:', error);
				enqueueSnackbar(t('exportFailedToGenerate', { ns: 'toast' }), { variant: 'error' });
			} finally {
				clearUserAction();
			}
		},
		[searchParams, enqueueSnackbar, t]
	);

	return (
		<>
			<KeysDataGrid
				key={'keys' + keysListType}
				showExportButton={allowsExporting}
				keysListType={keysListType}
				showTitle={false}
				enableMultiSelect={keysListType === 'unassigned'}
				enableAssignKey={keysListType === 'unassigned'}
				onAssignKeys={handleAssign}
				handleExportButtonClicked={() => setUserAction({ type: 'export' })}
			/>
			<KeyMultiAssignDialog
				open={userAction?.type === 'assignKeys'}
				loading={showModalLoader}
				onClose={clearUserAction}
				onFinish={handleAssignKeys}
			/>
			<FormDialog
				open={userAction?.type === 'export'}
				title={
					t('export', { ns: 'actions' }) +
					' ' +
					t('key.labels.plural', { ns: 'entities' }).toLowerCase()
				}
				loading={exportStatus === 'exporting'}
				submitLabel={t('export', { ns: 'actions' })}
			>
				<ExportForm
					status={exportStatus}
					selectedFormat={selectedFormat}
					onFormatChange={setSelectedFormat}
					onSubmit={handleExport}
					onCancel={clearUserAction}
				/>
			</FormDialog>
		</>
	);
};

export default KeysOverviewContainer;
